import { useMutation, useQueryClient } from '@tanstack/react-query';
import { installmentsQueryKey } from './keys';
import { normalizeInstallment, sortInstallments } from '../types';
import type {
  InstallmentDto,
  InstallmentsHttpRequest,
  ModifyInstallmentPayload,
  RawInstallmentDto,
} from '../types';

export interface UseModifyInstallmentParams {
  documentId: string;
  /** Endpoint base PUT (`/{id}/installments`); se le añade `/{installmentId}`. */
  modifyInstallmentEndpoint: string;
  httpRequest: InstallmentsHttpRequest;
}

export interface ModifyInstallmentInput {
  installmentId: string;
  payload: ModifyInstallmentPayload;
}

interface ModifyMutationContext {
  previous?: InstallmentDto[];
}

/**
 * Edita el monto (u otros campos) de una cuota
 * (`PUT /{id}/installments/{installmentId}`) con optimistic update.
 *
 * - `onMutate`: aplica el cambio a la fila y la marca `isManual = true`.
 * - `onError`: rollback al snapshot previo (el panel surfacea 409/422/403).
 * - `onSuccess`: reemplaza la fila con la respuesta del servidor (rowVersion++).
 * - `onSettled`: invalida `['document-installments', documentId]`.
 */
export function useModifyInstallment({
  documentId,
  modifyInstallmentEndpoint,
  httpRequest,
}: UseModifyInstallmentParams) {
  const queryClient = useQueryClient();
  const key = installmentsQueryKey(documentId);

  return useMutation<InstallmentDto, Error, ModifyInstallmentInput, ModifyMutationContext>({
    mutationFn: async ({ installmentId, payload }) => {
      const raw = await httpRequest<RawInstallmentDto>(
        'PUT',
        `${modifyInstallmentEndpoint}/${installmentId}`,
        payload,
      );
      return normalizeInstallment(raw);
    },
    onMutate: async ({ installmentId, payload }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<InstallmentDto[]>(key);

      queryClient.setQueryData<InstallmentDto[]>(key, (prev) =>
        (prev ?? []).map((row) =>
          row.id === installmentId
            ? {
                ...row,
                amount: payload.amount ?? row.amount,
                dueDate: payload.dueDate ?? row.dueDate,
                promptPaymentDate: payload.promptPaymentDate ?? row.promptPaymentDate,
                promptPaymentDiscount:
                  payload.promptPaymentDiscount ?? row.promptPaymentDiscount,
                isManual: true,
              }
            : row,
        ),
      );

      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(key, context.previous);
      }
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<InstallmentDto[]>(key, (prev) =>
        sortInstallments((prev ?? []).map((row) => (row.id === updated.id ? updated : row))),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
